import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import LeadForm from "@/components/LeadForm";
import { FadeIn, MaskedLines, Chapter } from "@/components/Motion";
import { IMAGES } from "@/data/content";

const SCOPE = [
  { title: "Industrial Design", text: "Distinctive product identity designed around your brand and market." },
  { title: "Engineering", text: "Mechanical, structural and rail engineering on the proven ACTIVE platform." },
  { title: "Electronics", text: "Control systems, displays, safety circuits and connectivity options." },
  { title: "Testing", text: "Endurance, environmental and safety testing to 50,000 cycles and beyond." },
  { title: "Certification", text: "EN 81-40 and CE conformity managed end-to-end by our compliance team." },
  { title: "Brand Identity", text: "Naming, colour systems and product graphics that make it unmistakably yours." },
  { title: "Packaging", text: "Retail and installer packaging designed for your logistics chain." },
  { title: "Production", text: "Pilot series, ramp-up and quality-gated serial manufacturing." },
  { title: "Mass Manufacturing", text: "Dedicated capacity with framework agreements for national programmes." },
];

const JOURNEY = [
  { phase: "01", title: "Define", text: "Market analysis, competitive positioning, target cost and product brief." },
  { phase: "02", title: "Design", text: "Industrial design, engineering and prototype iterations with your team." },
  { phase: "03", title: "Certify", text: "Testing, documentation and certification for your target markets." },
  { phase: "04", title: "Produce", text: "Mass manufacturing, branded packaging and European logistics." },
];

export default function ODM() {
  return (
    <>
      <PageHero
        overline="ODM Development"
        lines={["Launch Your Own", "Stairlift Brand."]}
        sub="From a blank page to a certified, mass-produced stairlift carrying your name. ACTIVE is the complete product development partner behind some of tomorrow's accessibility brands."
        image={IMAGES.roboticAssembly}
      />

      <section className="bg-white py-24" data-testid="odm-scope">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="01" label="Full Product Development" />
          <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950">
            <MaskedLines lines={["Everything a brand needs,", "under one roof"]} />
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCOPE.map((s, i) => (
              <FadeIn key={s.title} delay={(i % 3) * 0.07}>
                <div className="h-full rounded-2xl border border-slate-200/80 bg-[#F8F9FA] p-7 hover:border-signal/40 transition-colors duration-300" data-testid={`odm-scope-${i}`}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-950">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="odm-journey">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label="Development Journey" light />
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05] max-w-3xl">
            <MaskedLines lines={["Four phases.", "One finished brand."]} />
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {JOURNEY.map((j, i) => (
              <FadeIn key={j.phase} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl border border-white/10 bg-surface/60 p-7 pt-10 hover:border-signal/50 transition-colors duration-300" data-testid={`odm-phase-${i}`}>
                  <span className="absolute -top-4 left-7 font-display text-4xl font-bold tracking-tighter text-signal bg-obsidian pr-3">{j.phase}</span>
                  <h3 className="font-display text-xl font-semibold tracking-tight text-white">{j.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{j.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <div className="mt-12 spotlight-frame rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.machining} alt="Precision engineering at ACTIVE" loading="lazy" className="w-full h-[280px] sm:h-[400px] object-cover" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="odm-lead">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Chapter index="03" label="Start a Project" />
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={["Your brand,", "engineered in Europe"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
                ODM programmes begin with a confidential workshop. Share your ambitions and our development team will outline scope, timeline and investment.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <div className="rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-9">
              <LeadForm defaultType="odm" context="ODM page enquiry" />
            </div>
          </FadeIn>
        </div>
      </section>

      <CTABand
        overline="ODM"
        lines={["The next great stairlift", "brand could be yours."]}
        primary={{ to: "/contact?type=odm", label: "Launch Your Own Stairlift Brand", testid: "odm-cta-launch" }}
      />
    </>
  );
}
